// src/render/audio/music.ts — Phase 6 round-phase music swap (06-06).
//
// CONTEXT D-22: at minimum one warm/chill ambient loop swapped on
// `round-started` vs `round-ended`. Round-ended swaps to a wind-down variant
// for the diegetic team-switch window. Two manifest entries (calm + wind
// down) authored under the music/ asset directory. ~30–60s loops; volume
// P6_MUSIC_VOLUME (kept below ambient so dialogue waffle reads clean);
// crossfade duration P6_MUSIC_FADE_MS (= 800 ms).
//
// AUDIO-04 invariant: at least one calm loop + one wind-down loop exist;
// round-started crossfades calm in / wind-down out, round-ended crossfades
// the reverse. Howler.fade is the load-bearing primitive.
//
// BLOCKER-1 (Plan 03 trackHowl contract): both constructed Howls (calm +
// wind-down) are registered with audioManager.trackHowl so liveSources
// reflects reality (Plan 03 contract; AUDIO-07 ≤ 8 ceiling proof in 06-08).
// Cycle-safe at runtime — see ambient.ts header comment for the same pattern.
//
// Idempotency (T-6-06-02 mitigate): a module-level `calmPlaying` /
// `windDownPlaying` boolean guards back-to-back onRoundStarted calls. Without
// it, Howler `play()` on an already-playing loop creates a fresh playback id
// and stacks volume; the guard makes the swap idempotent.
//
// Source: 06-CONTEXT.md D-22; 06-PATTERNS.md `src/render/audio/music.ts`
// row; 06-RESEARCH.md Pattern 3 (round-transition swap with Howler.fade).

import { Howl } from 'howler';

import type { AudioCodec, AudioManifest } from './codec';
import type { RenderEventMap } from '../bus';
import { srcListFor } from './codec';
import { audioManager } from './manager';
import {
  P6_MUSIC_VOLUME,
  P6_MUSIC_FADE_MS,
} from './constants';

// ---------------------------------------------------------------------------
// Module-level state (singleton — D-22)
// ---------------------------------------------------------------------------

let calmHowl: Howl | null = null;
let windDownHowl: Howl | null = null;

/**
 * Idempotency guards — onRoundStarted/onRoundEnded both invoke `play()` on a
 * fresh-into-the-active-side loop. Without these flags, a back-to-back
 * onRoundStarted would call play() twice, stacking concurrent playback ids
 * and ramping volume past P6_MUSIC_VOLUME. The guard short-circuits the
 * second call so the active side stays at one in-flight playback id.
 */
let calmPlaying = false;
let windDownPlaying = false;

// ---------------------------------------------------------------------------
// Public API (matches 06-03 manager.ts seam)
// ---------------------------------------------------------------------------

export function initMusic(_codec: AudioCodec, manifest: AudioManifest): void {
  calmHowl = new Howl({
    src: srcListFor('music/calm-loop', manifest),
    loop: true,
    pool: 1,
    preload: false,
    volume: 0,
  });
  windDownHowl = new Howl({
    src: srcListFor('music/wind-down-loop', manifest),
    loop: true,
    pool: 1,
    preload: false,
    volume: 0,
  });
  calmHowl.load();
  windDownHowl.load();
  // BLOCKER-1: register both Howls with manager liveSources Set
  // (Plan 03 trackHowl contract). UI-06: both loops live on the 'music' bus at
  // design volume P6_MUSIC_VOLUME so the Music slider silences only the score.
  audioManager.trackHowl(calmHowl, 'music', P6_MUSIC_VOLUME);
  audioManager.trackHowl(windDownHowl, 'music', P6_MUSIC_VOLUME);
}

/**
 * Forwarded by manager.ts from the renderBus 'round-started' subscriber.
 * Crossfade: calm fades in 0 → P6_MUSIC_VOLUME; wind-down fades out
 * (currentVol → 0) and stops once the fade completes.
 */
export function onRoundStarted(_e: RenderEventMap['round-started']): void {
  // Wind-down side: fade-out from current volume to 0, then stop. Fires
  // unconditionally so the swap is symmetric — if wind-down was silent the
  // fade is a no-op (0 → 0 over P6_MUSIC_FADE_MS).
  if (windDownHowl) {
    const currentVol = (windDownHowl.volume() as number) || 0;
    windDownHowl.fade(currentVol, 0, P6_MUSIC_FADE_MS);
    setTimeout(() => windDownHowl?.stop(), P6_MUSIC_FADE_MS);
    windDownPlaying = false;
  }

  // Calm side: idempotent guard — only play if not already playing.
  if (!calmHowl || calmPlaying) return;
  calmHowl.volume(0);
  calmHowl.play();
  // UI-06: fade to the design volume scaled by the LIVE music bus gain so the
  // score lands at the Music slider's level. Pitfall A: a mid-fade slider move
  // lands on the next fade event (known minor).
  calmHowl.fade(0, P6_MUSIC_VOLUME * audioManager.getBusGain('music'), P6_MUSIC_FADE_MS);
  calmPlaying = true;
}

/**
 * Forwarded by manager.ts from the renderBus 'round-ended' subscriber.
 * Mirror of onRoundStarted: calm fades out, wind-down fades in.
 */
export function onRoundEnded(_e: RenderEventMap['round-ended']): void {
  // Calm side: fade-out from current volume to 0, then stop. Symmetric with
  // the onRoundStarted wind-down fade-out — fires unconditionally.
  if (calmHowl) {
    const currentVol = (calmHowl.volume() as number) || 0;
    calmHowl.fade(currentVol, 0, P6_MUSIC_FADE_MS);
    setTimeout(() => calmHowl?.stop(), P6_MUSIC_FADE_MS);
    calmPlaying = false;
  }

  // Wind-down side: idempotent guard.
  if (!windDownHowl || windDownPlaying) return;
  windDownHowl.volume(0);
  windDownHowl.play();
  // UI-06: fade target scaled by the live music bus gain (Pitfall A minor).
  windDownHowl.fade(0, P6_MUSIC_VOLUME * audioManager.getBusGain('music'), P6_MUSIC_FADE_MS);
  windDownPlaying = true;
}

export function destroyMusic(): void {
  calmHowl?.unload();
  windDownHowl?.unload();
  calmHowl = null;
  windDownHowl = null;
  calmPlaying = false;
  windDownPlaying = false;
}
